import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Helper funkcija za dobijanje trenutnog tokena u headeru (koristimo je samo za fetch/sendBeacon pozive)
const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

interface EditSession {
  sessionId: string;
  canEdit: boolean;
  message: string;
}

interface SessionWarning {
  inUse: boolean;
  isOwner: boolean;
  currentEditor?: {
    username: string;
    userRole: string;
    startedAt: string;
    lastActivity: string;
  };
  canTakeOver: boolean;
  sessionId?: string;
}

interface UseEditSessionProps {
  resourceId: string;
  resourceType: 'measurement' | 'product' | 'template';
  resourceName: string;
}

export const useEditSession = ({ resourceId, resourceType, resourceName }: UseEditSessionProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [warning, setWarning] = useState<SessionWarning | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionTakenOver, setSessionTakenOver] = useState(false);

  // Proveri da li je resurs u upotrebi
  const checkSession = useCallback(async () => {
    try {
      const response = await api.get<SessionWarning>(`/active-sessions/check/${resourceId}`, {
        params: {
          resourceType,
          _t: Date.now(),
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (response.status === 200) {
        const data = response.data;
        
        // Proveri da li je sesija stvarno aktivna
        if (data.inUse && data.currentEditor?.lastActivity) {
          const sessionAge = Date.now() - new Date(data.currentEditor.lastActivity).getTime();
          if (sessionAge > 5 * 60 * 1000) { // 5 minuta
            // console.log('🔍 Session expired in checkSession, clearing warning');
            setWarning(null);
            return { inUse: false };
          }
        }
        
        setWarning(data.inUse ? data : null);
        return data;
      }
    } catch (err) {
      // console.error('Greška pri proveri sesije:', err);
    }
    return null;
  }, [resourceId, resourceType]);

  // Započni sesiju editovanja
  const startSession = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<EditSession>('/active-sessions/start', {
        resourceId,
        resourceType,
        resourceName,
      });

      if (response.status === 200) {
        const data = response.data;
        setSessionId(data.sessionId);
        setIsEditing(true);
        setWarning(null);
        setSessionTakenOver(false);
        return true;
      } else if (response.status === 409) {
        // Resurs je već u upotrebi
        const data = response.data as any;
        // console.log('🔍 Conflict data:', data);
        
        // Proveri da li je sesija stvarno aktivna
        if (data.currentEditor?.lastActivity) {
          const sessionAge = Date.now() - new Date(data.currentEditor.lastActivity).getTime();
          if (sessionAge > 5 * 60 * 1000) { // 5 minuta
            // console.log('🔍 Session expired in startSession conflict, retrying...');
            // Pokušaj ponovo da započneš sesiju
            return await startSession();
          }
        }
        
        setWarning({
          inUse: true,
          isOwner: false,
          currentEditor: data.currentEditor || null,
          canTakeOver: data.canTakeOver || false,
          sessionId: data.sessionId || null
        });
        return false;
      } 
      return false;
    } catch (err) {
      setError('Greška pri započinjanju sesije');
      return false;
    } finally {
      setLoading(false);
    }
  }, [resourceId, resourceType, resourceName]);

  // Preuzmi sesiju (samo admin)
  const takeOverSession = useCallback(async (): Promise<boolean> => {
    if (!warning?.sessionId) return false;

    // Proveri da li je sesija stvarno aktivna pre take over-a
    if (warning.currentEditor?.lastActivity) {
      const sessionAge = Date.now() - new Date(warning.currentEditor.lastActivity).getTime();
      if (sessionAge > 5 * 60 * 1000) { // 5 minuta
        // console.log('🔍 Session expired before take over, sessionId:', warning.sessionId);
        setError('Sesija je istekla i ne može se preuzeti');
        return false;
      }
    }

    // console.log('🔍 takeOverSession called with sessionId:', warning.sessionId);
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/active-sessions/take-over', {
        sessionId: warning.sessionId,
      }, {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (response.status === 200) {
        const data = response.data;
        // console.log('🔍 Take over response:', data);
        setSessionId(data.session.sessionId);
        setIsEditing(true);
        setWarning(null);
        setSessionTakenOver(false);
        // console.log('🔍 Take over successful, new sessionId:', data.session.sessionId);
        // Admin je sada u edit modu i može da nastavi sa editovanjem
        return true;
      } else {
        const errorData = response.data;
        // console.error('🔍 Take over failed:', errorData);
        if (errorData.error === 'Sesija je istekla i ne može se preuzeti') {
          setError('Sesija je istekla i ne može se preuzeti. Pokušajte ponovo da započnete editovanje.');
          setWarning(null);
        } else {
          setError(errorData.error || 'Greška pri preuzimanju sesije');
        }
        return false;
      }
    } catch (err) {
      // console.error('🔍 Take over error:', err);
      setError('Greška pri preuzimanju sesije');
      return false;
    } finally {
      setLoading(false);
    }
  }, [warning?.sessionId, warning?.currentEditor?.lastActivity]);

  // Završi sesiju
  const endSession = useCallback(async () => {
    if (!sessionId) return;

    // console.log('🔍 Ending session:', sessionId);

    try {
      const response = await api.post('/active-sessions/end', { sessionId });

      if (response.status === 200) {
        // console.log('🔍 Session ended successfully:', sessionId);
      } else {
        const errorData: any = response.data;
        if (errorData?.message === 'Sesija je već istekla') {
          // console.log('🔍 Session already expired during end:', sessionId);
        } else {
          // console.error('🔍 Failed to end session:', sessionId, errorData);
        }
      }
    } catch (err) {
      // console.error('Greška pri završavanju sesije:', err);
    } finally {
      setSessionId(null);
      setIsEditing(false);
      setSessionTakenOver(false);
    }
  }, [sessionId]);

  // Heartbeat - ažuriraj aktivnost
  const sendHeartbeat = useCallback(async () => {
    if (!sessionId) return;

    try {
      const response = await api.post('/active-sessions/heartbeat', { sessionId });

      if (response.status !== 200) {
        const errorData = response.data;
        if (errorData.error === 'Sesija je istekla') {
          // console.log('🔍 Session expired during heartbeat, clearing session');
          setSessionId(null);
          setIsEditing(false);
          setSessionTakenOver(false);
        }
      }
    } catch (err) {
      // console.error('Greška pri slanju heartbeat-a:', err);
    }
  }, [sessionId]);

  // Proveri da li je sesija preuzeta (za kontrolora)
  const checkIfSessionTakenOver = useCallback(async () => {
    if (!sessionId) return;

    // console.log('🔍 checkIfSessionTakenOver called for sessionId:', sessionId);
    
    try {
      const response = await api.get<SessionWarning>(`/active-sessions/check/${resourceId}`, {
        params: {
          resourceType,
          _t: Date.now(),
        },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (response.status === 200) {
        const data = response.data;
        // console.log('🔍 checkIfSessionTakenOver response:', data);
        
        if (data.inUse && !data.isOwner) {
          // Proveri da li je sesija stvarno aktivna
          if (data.currentEditor?.lastActivity) {
            const sessionAge = Date.now() - new Date(data.currentEditor.lastActivity).getTime();
            if (sessionAge > 5 * 60 * 1000) { // 5 minuta
              // console.log('🔍 Session expired in checkIfSessionTakenOver, clearing session');
              setSessionTakenOver(false);
              setIsEditing(false);
              setSessionId(null);
              return false;
            }
          }
          
          // Sesija je preuzeta od strane drugog korisnika
          // console.log('🔍 Session taken over detected!');
          setSessionTakenOver(true);
          setIsEditing(false);
          setSessionId(null);
          return true;
        }
      }
    } catch (err) {
      // console.error('Greška pri proveri preuzimanja sesije:', err);
    }
    return false;
  }, [sessionId, resourceId, resourceType]);

  // Automatski heartbeat svakih 15 sekundi (smanjeno sa 30)
  useEffect(() => {
    if (!isEditing || !sessionId) return;

    const interval = setInterval(sendHeartbeat, 15000);
    return () => clearInterval(interval);
  }, [isEditing, sessionId, sendHeartbeat]);

  // Proveri da li je sesija preuzeta svakih 5 sekundi (smanjeno sa 10)
  useEffect(() => {
    if (!isEditing || !sessionId) return;

    // console.log('🔍 Setting up session takeover check interval (5s) for sessionId:', sessionId);
    const interval = setInterval(() => {
      // console.log('🔍 Checking for session takeover...');
      checkIfSessionTakenOver();
    }, 5000);
    return () => {
      // console.log('🔍 Clearing session takeover check interval');
      clearInterval(interval);
    };
  }, [isEditing, sessionId, checkIfSessionTakenOver]);

  // Automatsko završavanje sesije kada se komponenta unmount-uje
  useEffect(() => {
    return () => {
      if (sessionId) {
        // console.log('🔍 Component unmounting, ending session:', sessionId);
        // Koristi sendBeacon za pouzdano završavanje sesije
        navigator.sendBeacon('/api/active-sessions/end-beacon', JSON.stringify({ sessionId }));
      }
    };
  }, [sessionId]);

  // Dodaj beforeunload event listener za pouzdano završavanje sesije
  useEffect(() => {
    if (!sessionId) return;

    const handleBeforeUnload = () => {
      // console.log('🔍 Beforeunload event, ending session:', sessionId);
      // Koristi sendBeacon za pouzdano završavanje sesije
      navigator.sendBeacon('/api/active-sessions/end-beacon', JSON.stringify({ sessionId }));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sessionId]);

  return {
    sessionId,
    isEditing,
    warning,
    loading,
    error,
    sessionTakenOver,
    startSession,
    takeOverSession,
    endSession,
    checkSession
  };
}; 